/**
 * @fileoverview Shared file-copy freshness clock for migration retry and UI.
 * @module sync-config/rehostCopyFreshness
 *
 * Responsibilities:
 * - Decide whether a running file-copy attempt is stale.
 * - Ignore a metadata-migration heartbeat that predates this copy attempt.
 * Dependencies: none.
 * Architecture: Pure function. API retry guard and system-web badge both import this so the clocks cannot drift.
 * Notes: Missing both timestamps is not stale — the copy may not have reported yet.
 */

/**
 * A copy worker that has not written a heartbeat for this long is dead.
 * The UI offers Copy remaining files at the same moment the retry route accepts.
 */
export const REHOST_COPY_STALE_MS = 2 * 60 * 60 * 1000;

/**
 * @contract RehostCopyRunClock
 * Purpose: The only run fields the copy freshness clock reads.
 * Fields: rehostStatus, heartbeatAt, rehostStartedAt, updatedAt.
 * Rules: A heartbeat older than rehostStartedAt belongs to metadata migration, not this copy.
 * Sync: Read from the already-loaded run document; no fetch.
 */
export interface RehostCopyRunClock {
  rehostStatus?: string;
  heartbeatAt?: Date | string;
  rehostStartedAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * @process isRehostCopyStale
 * Steps: Ignore non-running copies, pick the copy-era activity timestamp, then compare it to the stale window.
 * Inputs: Run clock, current epoch milliseconds, and the stale window.
 * Outputs: True when the running copy has gone quiet longer than the window.
 * Failure Handling: Unparseable timestamps fall through to the next reference; none present means not stale.
 * Idempotency: REQUIRED — equal inputs return the same boolean.
 */
export function isRehostCopyStale(
  run: RehostCopyRunClock,
  nowMs: number = Date.now(),
  staleMs: number = REHOST_COPY_STALE_MS,
): boolean {
  if (run.rehostStatus !== 'running') return false;
  const reference = copyActivityReference(run);
  if (!reference) return false;
  const timestamp = timestampMs(reference);
  if (timestamp === null) return false;
  return nowMs - timestamp > staleMs;
}

function copyActivityReference(run: RehostCopyRunClock): Date | string | undefined {
  const started = timestampMs(run.rehostStartedAt);
  const heartbeat = timestampMs(run.heartbeatAt);
  if (heartbeat !== null && (started === null || heartbeat >= started)) return run.heartbeatAt;
  return run.rehostStartedAt ?? run.updatedAt;
}

function timestampMs(value: Date | string | undefined): number | null {
  if (!value) return null;
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}
